import { useState, useEffect } from 'react'
import { Modal, TextField, BlockStack, Checkbox, Select, InlineStack, Text } from '@shopify/polaris'

export type DiscountData = {
    code: string
    applyAutomatic: boolean
    customDiscount: boolean
    discountType: 'amount' | 'percentage'
    discountValue: string
    reason: string
}

type Props = {
    open: boolean
    discount: DiscountData
    onSave: (discount: DiscountData) => void
    onClose: () => void
}

export function DiscountModal({ open, discount: initial, onSave, onClose }: Props) {
    const [discount, setDiscount] = useState<DiscountData>(initial)
    const [error, setError] = useState('')

    useEffect(() => {
        if (open) setDiscount(initial)
    }, [open, initial])

    const update = (field: keyof DiscountData) => (value: any) => {
        setError('')
        setDiscount(prev => ({ ...prev, [field]: value }))
    }

    const handleDone = () => {
        if (discount.customDiscount) {
            const val = parseFloat(discount.discountValue || '0')
            if (val < 0) {
                setError('Value cannot be negative')
                return
            }
            if (discount.discountType === 'percentage' && val > 100) {
                setError('Percentage cannot exceed 100%')
                return
            }
        }
        setError('')
        onSave(discount)
        onClose()
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add discount"
            primaryAction={{ content: 'Done', onAction: handleDone }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
        >
            <Modal.Section>
                <BlockStack gap="400">
                    <Text as="p" fontWeight="bold">Discount codes</Text>
                    <TextField
                        label="Discount code"
                        labelHidden
                        placeholder="Enter a discount code"
                        value={discount.code}
                        onChange={update('code')}
                        autoComplete="off"
                    />
                    <Checkbox
                        label="Apply all eligible automatic discounts"
                        checked={discount.applyAutomatic}
                        onChange={update('applyAutomatic')}
                    />
                    <Checkbox
                        label="Add custom order discount"
                        checked={discount.customDiscount}
                        onChange={update('customDiscount')}
                    />
                    {discount.customDiscount && (
                        <BlockStack gap="400">
                            <InlineStack gap="400">
                                <div style={{ flex: 1 }}>
                                    <Select
                                        label="Discount type"
                                        options={[
                                            { label: 'Amount', value: 'amount' },
                                            { label: 'Percentage', value: 'percentage' },
                                        ]}
                                        value={discount.discountType}
                                        onChange={update('discountType')}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <TextField
                                        label="Discount value"
                                        value={discount.discountValue}
                                        onChange={update('discountValue')}
                                        autoComplete="off"
                                        type="number"
                                        prefix={discount.discountType === 'amount' ? '€' : undefined}
                                        suffix={discount.discountType === 'percentage' ? '%' : 'EUR'}
                                        error={error}
                                    />
                                </div>
                            </InlineStack>
                            <TextField
                                label="Reason for discount"
                                value={discount.reason}
                                onChange={update('reason')}
                                autoComplete="off"
                                helpText="Visible to customer"
                            />
                        </BlockStack>
                    )}
                </BlockStack>
            </Modal.Section>
        </Modal>
    )
}
